import type { AnyAction, Middleware } from '@reduxjs/toolkit'
import { isRejectedWithValue } from '@reduxjs/toolkit'
import { customApi } from 'shared/api/customApi'

const AUTH_ENDPOINTS = [
    'login',
    'signUp',
    'resetPassword',
    'resetPasswordConfirmation',
] as const

function getRefreshToken(): string {
    // Check sessionStorage first — matches the priority used by baseApi.prepareHeaders.
    // localStorage-first would pick up stale tokens from expired "Remember Me" sessions
    // when the user has since logged in without "Remember Me" (tokens in sessionStorage).
    const raw =
        sessionStorage.getItem('refreshToken') ||
        localStorage.getItem('refreshToken')
    if (!raw) return ''
    try {
        const parsed = JSON.parse(raw)
        return typeof parsed === 'string' ? parsed : ''
    } catch {
        return raw
    }
}

function clearAuthStorage(): void {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    sessionStorage.removeItem('accessToken')
    sessionStorage.removeItem('refreshToken')
}

function isAuthErrorAction(action: AnyAction): boolean {
    if (!isRejectedWithValue(action)) return false
    const payload = action.payload as { status?: number } | undefined
    return payload?.status === 401
}

function getEndpointName(action: AnyAction): string {
    return action.meta?.arg?.endpointName ?? ''
}

export const errorMiddleware: Middleware = ({ dispatch }) => {
    return (next) => async (action: AnyAction) => {
        if (!isAuthErrorAction(action)) {
            return next(action)
        }

        const endpointName = getEndpointName(action)

        if (
            AUTH_ENDPOINTS.includes(
                endpointName as (typeof AUTH_ENDPOINTS)[number]
            )
        ) {
            return next(action)
        }

        const refreshToken = getRefreshToken()

        if (!refreshToken) {
            window.location.href = '/login'
            return
        }

        // RTK Middleware type provides Dispatch<AnyAction> which lacks ThunkDispatch
        // overloads needed for `initiate()` thunks. No safe narrowing exists here.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resultAction = (await (dispatch as any)(
            customApi.endpoints.refreshToken.initiate(refreshToken)
        )) as { data?: { accessToken?: string } }

        if (resultAction.data?.accessToken) {
            window.location.reload()
            return
        }

        // Refresh failed — clear stale tokens so the next login starts clean
        clearAuthStorage()
        window.location.href = '/login'
    }
}
